import {
  type InfiniteData,
  type QueryClient,
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import { graphql } from '@/gql'
import type { UploadedAttachment } from '@/features/attachments/api'
import type { MessagesQuery } from '@/gql/graphql'
import { gqlClient } from '@/lib/graphql'
import { subscribe } from '@/lib/wsClient'
import { clearUnread } from '@/features/activity/api'
import { applyReaction } from '@/features/reactions/api'
import { loungeKeys } from '@/features/lounges/api'
import { nookKeys } from '@/features/nooks/api'

/**
 * Every message: the history, sending and changing it, and the socket that
 * keeps a nook live. Components read one hook and never learn a cache exists.
 */

export const messageKeys = {
  all: ['messages'] as const,
  timeline: (nookId: string) => [...messageKeys.all, 'timeline', nookId] as const,
}

/** One message as the timeline renders it. */
export type Message = MessagesQuery['messages']['messages'][number]

/**
 * A message the server has not acknowledged yet.
 *
 * `pending` and `failed` exist only on the client. They are what lets the
 * composer clear the instant you press enter without pretending the send has
 * succeeded.
 */
export type TimelineMessage = Message & {
  pending?: boolean
  failed?: boolean
  /** Kept on a failed send so the retry can resend exactly what was typed. */
  draft?: { content: string; replyTo: string | null; attachments: UploadedAttachment[] }
}

type Page = MessagesQuery
type Timeline = InfiniteData<Page, string | null>

const MessagesQueryDocument = graphql(`
  query Messages($nookId: UUID!, $before: UUID, $limit: Int!) {
    messages(nookId: $nookId, before: $before, limit: $limit) {
      hasMore
      messages {
        id
        nookId
        content
        mentions
        editedAt
        deletedAt
        createdAt
        attachments {
          id
          filename
          mime
          byteSize
          width
          height
          isImage
          url
        }
        reactions {
          emoji
          count
          mine
        }
        author {
          id
          handle
          displayName
          avatarUrl
        }
        replyTo {
          id
          content
          deletedAt
          author {
            id
            handle
            displayName
          }
        }
      }
    }
  }
`)

const SendMessageMutation = graphql(`
  mutation SendMessage(
    $nookId: UUID!
    $content: String!
    $replyTo: UUID
    $attachmentIds: [UUID!]
    $clientNonce: String
  ) {
    sendMessage(
      nookId: $nookId
      content: $content
      replyTo: $replyTo
      attachmentIds: $attachmentIds
      clientNonce: $clientNonce
    ) {
      id
      nookId
      content
      mentions
      editedAt
      deletedAt
      createdAt
      attachments {
        id
        filename
        mime
        byteSize
        width
        height
        isImage
        url
      }
      reactions {
        emoji
        count
        mine
      }
      author {
        id
        handle
        displayName
        avatarUrl
      }
      replyTo {
        id
        content
        deletedAt
        author {
          id
          handle
          displayName
        }
      }
    }
  }
`)

const EditMessageMutation = graphql(`
  mutation EditMessage($messageId: UUID!, $content: String!) {
    editMessage(messageId: $messageId, content: $content) {
      id
      content
      mentions
      editedAt
    }
  }
`)

const DeleteMessageMutation = graphql(`
  mutation DeleteMessage($messageId: UUID!) {
    deleteMessage(messageId: $messageId) {
      id
      content
      deletedAt
    }
  }
`)

const MarkReadMutation = graphql(`
  mutation MarkRead($nookId: UUID!, $messageId: UUID!) {
    markRead(nookId: $nookId, messageId: $messageId)
  }
`)

const TypingMutation = graphql(`
  mutation Typing($nookId: UUID!) {
    typing(nookId: $nookId)
  }
`)

const TypingStreamSubscription = graphql(`
  subscription TypingStream($nookId: UUID!) {
    typingStream(nookId: $nookId) {
      nookId
      user {
        id
        handle
        displayName
      }
    }
  }
`)

const MessageStreamSubscription = graphql(`
  subscription MessageStream($nookId: UUID!) {
    messageStream(nookId: $nookId) {
      kind
      clientNonce
      reaction {
        messageId
        emoji
        userId
        added
      }
      message {
        id
        nookId
        content
        mentions
        editedAt
        deletedAt
        createdAt
        attachments {
          id
          filename
          mime
          byteSize
          width
          height
          isImage
          url
        }
        reactions {
          emoji
          count
          mine
        }
        author {
          id
          handle
          displayName
          avatarUrl
        }
        replyTo {
          id
          content
          deletedAt
          author {
            id
            handle
            displayName
          }
        }
      }
    }
  }
`)

const PAGE_SIZE = 50

export const messagesInfiniteQueryOptions = (nookId: string) =>
  infiniteQueryOptions({
    queryKey: messageKeys.timeline(nookId),
    queryFn: ({ pageParam }) =>
      gqlClient.request(MessagesQueryDocument, {
        nookId,
        before: pageParam,
        limit: PAGE_SIZE,
      }),
    initialPageParam: null as string | null,
    // Pages run newest-first, so "older" is the last id on the last page — and
    // message ids are UUIDv7, which is exactly why an id works as a cursor.
    getNextPageParam: (lastPage) => {
      if (!lastPage.messages.hasMore) return undefined
      const oldest = lastPage.messages.messages.at(-1)
      return oldest?.id ?? undefined
    },
    staleTime: 30_000,
  })

export function useMessages(nookId: string) {
  return useInfiniteQuery(messagesInfiniteQueryOptions(nookId))
}

/**
 * Every message in a nook, newest first, flattened out of its pages.
 *
 * Deduplicated by id: an optimistic message and the real one briefly coexist
 * when the socket echo arrives before the mutation resolves, and the timeline
 * must never show both.
 */
export function useTimeline(nookId: string) {
  const query = useMessages(nookId)

  const seen = new Set<string>()
  const messages: TimelineMessage[] = []

  for (const page of query.data?.pages ?? []) {
    for (const message of page.messages.messages as TimelineMessage[]) {
      if (seen.has(message.id)) continue
      seen.add(message.id)
      messages.push(message)
    }
  }

  return { ...query, messages }
}

export interface SendInput {
  content: string
  replyTo?: string | null
  /** The signed-in user, so the pending message can render as theirs. */
  author: NonNullable<Message['author']>
  /** Already uploaded — bytes never travel through GraphQL. */
  attachments?: UploadedAttachment[]
}

/**
 * One attempt at a send, with the nonce that ties its three parties together.
 *
 * Minted here rather than by a component: it is cache plumbing, and it has to be
 * the same string in the mutation, in the optimistic row and in the event that
 * comes back — which is why it lives in the variables rather than in `onMutate`'s
 * context, where the request could not see it.
 */
interface SendCommand extends SendInput {
  nonce: string
}

/**
 * Send a message, and show it before the server has agreed.
 *
 * **Three writers, one row, and a nonce is what keeps them from becoming two
 * rows.** `onMutate` draws the message immediately with an id it invented; the
 * server mints a UUIDv7 and publishes a `CREATED` event to the whole nook —
 * including back to us, because a topic cannot exclude one subscriber — and that
 * event routinely *beats* the mutation's own response, since the publish happens
 * before the resolver serialises its reply. Nothing relates the id we invented to
 * the one the server minted, so without a correlation string the echo arrives as
 * a stranger, gets prepended beside our own copy, and the response then deletes
 * the pending one a moment later. Two copies, then a jump: the bug this exists to
 * remove.
 *
 * So the nonce goes out with the send and comes back on the event. Whichever of
 * the echo and the response arrives first swaps the pending row for the real one
 * *in place*; the loser is a no-op. It also fixes the case where the server
 * succeeds and the response is lost — the echo lands, and there is no orphaned
 * "Not sent." copy left to retry.
 *
 * The two fixes not taken, because both are worse: dropping our own author id
 * from the stream would mean a message sent from a phone never reached the
 * desktop, and letting the client mint the real id would hand it a pagination
 * cursor.
 */
export function useSendMessage(nookId: string) {
  const client = useQueryClient()

  const mutation = useMutation({
    mutationFn: ({ content, replyTo, attachments, nonce }: SendCommand) =>
      gqlClient.request(SendMessageMutation, {
        nookId,
        content,
        replyTo: replyTo ?? null,
        attachmentIds: (attachments ?? []).map((file) => file.id),
        clientNonce: nonce,
      }),

    // The optimistic triad. The pending message goes in at the top of the newest
    // page keyed by the nonce; whoever gets here first replaces it by that key.
    onMutate: async (input) => {
      await client.cancelQueries({ queryKey: messageKeys.timeline(nookId) })

      const pendingId = input.nonce
      const pending: TimelineMessage = {
        id: pendingId,
        nookId,
        content: input.content,
        mentions: [],
        editedAt: null,
        deletedAt: null,
        createdAt: new Date().toISOString(),
        author: input.author,
        replyTo: null,
        // Rendered from the upload response, so a picture is on screen the
        // instant you press enter rather than after the round trip.
        attachments: input.attachments ?? [],
        // Nobody can have reacted to something that has not arrived yet.
        reactions: [],
        pending: true,
        draft: {
          content: input.content,
          replyTo: input.replyTo ?? null,
          attachments: input.attachments ?? [],
        },
      }

      prependMessage(client, nookId, pending)
      return { pendingId }
    },

    onSuccess: (data, _input, context) => {
      // Replace at the pending row's position rather than removing and
      // re-prepending, so the message keeps its place in the order. A no-op when
      // the socket's echo already did this, which is the common case.
      replaceMessage(client, nookId, context.pendingId, data.sendMessage as TimelineMessage)
    },

    onError: (_error, _input, context) => {
      if (!context) return
      // Only if it is still pending. A lost response over a send the server
      // actually took leaves the echo's real message here instead, and marking
      // *that* as failed would offer to send it twice.
      updateMessage(client, nookId, context.pendingId, (message) => ({
        ...message,
        pending: false,
        failed: true,
      }))
    },
  })

  // The nonce is minted per attempt, so a retry is a new correlation rather than
  // a second claim on the first one.
  const mutate = useCallback(
    (input: SendInput) => mutation.mutate({ ...input, nonce: crypto.randomUUID() }),
    [mutation.mutate],
  )

  return { ...mutation, mutate }
}

/** Resend a message whose first attempt failed, and drop the failed copy. */
export function useRetrySend(nookId: string) {
  const client = useQueryClient()
  const send = useSendMessage(nookId)

  return (message: TimelineMessage) => {
    if (!message.draft || !message.author) return

    removeMessage(client, nookId, message.id)
    send.mutate({
      content: message.draft.content,
      replyTo: message.draft.replyTo,
      author: message.author,
      attachments: message.draft.attachments,
    })
  }
}

export function useEditMessage(nookId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { messageId: string; content: string }) =>
      gqlClient.request(EditMessageMutation, input),
    onSuccess: (data) => {
      updateMessage(client, nookId, data.editMessage.id, (message) => ({
        ...message,
        ...data.editMessage,
      }))
    },
  })
}

export function useDeleteMessage(nookId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (messageId: string) => gqlClient.request(DeleteMessageMutation, { messageId }),
    // The server answers with the tombstone, so the row stays and only its
    // content goes — replies to it keep rendering their quote.
    onSuccess: (data) => {
      updateMessage(client, nookId, data.deleteMessage.id, (message) => ({
        ...message,
        ...data.deleteMessage,
      }))
    },
  })
}

export function useMarkRead(nookId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (messageId: string) => gqlClient.request(MarkReadMutation, { nookId, messageId }),

    // Before the round trip: catching up is a direct manipulation, and a badge
    // that lingers for the length of a request reads as the mark not having
    // taken. Clears the lounge's total by the same amount, or the rail dot
    // would outlive the room badge that caused it.
    onMutate: () => clearUnread(client, nookId),

    // `onSettled`, not `onSuccess`: a *failed* mark has to put the real numbers
    // back too, and the server is the only thing that knows them.
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: nookKeys.lists() }),
        client.invalidateQueries({ queryKey: loungeKeys.mine() }),
      ])
    },
  })
}

/**
 * Keep one nook's timeline live for as long as the component is mounted.
 */
export function useMessageStream(nookId: string, myId: string | undefined) {
  const client = useQueryClient()

  useEffect(() => {
    return subscribe(
      MessageStreamSubscription,
      { nookId },
      {
        onNext: ({ messageStream: event }) => {
          // Exactly one of `message` and `reaction` is set, decided by `kind`.
          //
          // A `REACTED` event carries the delta rather than the message,
          // because `Reaction.mine` is per-viewer and this stream is read by
          // everybody in the room — so each browser folds it in and answers
          // `mine` about itself.
          if (event.reaction) {
            applyReaction(client, nookId, event.reaction, myId)
            // Deliberately no nook-list invalidate: a thumbs-up is not unread.
            return
          }

          // Written as a guard rather than a cast on purpose — the cast that
          // used to be here would have compiled straight through the schema
          // change and gone wrong the first time somebody reacted.
          const message = event.message as TimelineMessage | null
          if (!message) return

          if (event.kind === 'CREATED') {
            // Our own send is already on screen from `onMutate`, drawn under a
            // nonce, and this echo carries that nonce back — so it *replaces*
            // that row rather than landing beside it. Whichever of this and the
            // mutation's response arrives first wins and the other is a no-op.
            //
            // A send from another tab of ours has no pending row here, and
            // `replaceMessage` handles that too: nothing matches the nonce, the
            // real message is not present, so it prepends. That is why the fix
            // is a nonce and not an author filter — a filter would have made
            // this case disappear entirely.
            if (event.clientNonce) {
              replaceMessage(client, nookId, event.clientNonce, message)
            } else {
              prependMessage(client, nookId, message)
            }
          } else {
            updateMessage(client, nookId, message.id, () => message)
          }

          // Kept, even though `activityStream` now carries badges for every
          // other room. This one covers the room you have *open*, which that
          // stream deliberately skips to avoid flashing a badge onto what you
          // are already reading — and "open" is not the same as "caught up":
          // scrolled up, or with the tab in the background, `markRead` does not
          // fire and these messages really are unread. Costs one nook-list
          // refetch per message in the focused room.
          //
          // Not for our own words, though: the unread query excludes them
          // (`author_id IS DISTINCT FROM $1`), so a refetch here could only ever
          // return the number already on screen. That is D10's rule, applied on
          // this side of the socket too.
          if (message.author?.id !== myId) {
            void client.invalidateQueries({ queryKey: nookKeys.all })
          }
        },
        // A restart means an unknown gap, so the newest page is refetched
        // whole rather than trusting what is cached.
        onStart: () => {
          void client.invalidateQueries({ queryKey: messageKeys.timeline(nookId) })
        },
      },
    )
  }, [client, nookId, myId])
}

/** How long between typing pings on the wire, however fast somebody types. */
const TYPING_THROTTLE = 3_000
/** How long a name stays in the indicator after their last ping. */
const TYPING_TTL = 6_000

/**
 * A throttled "I am typing" ping.
 *
 * Returns a function safe to call on every keystroke: it sends at most one
 * request every few seconds. The mutation is fire-and-forget — a typing
 * indicator that reported its own failure would be worse than one that
 * silently misses a beat.
 */
export function useTypingPing(nookId: string) {
  const lastSent = useRef(0)

  const mutation = useMutation({
    mutationFn: () => gqlClient.request(TypingMutation, { nookId }),
    onError: () => {},
  })

  // A new room starts its own clock.
  useEffect(() => {
    lastSent.current = 0
  }, [nookId])

  return useCallback(() => {
    const now = Date.now()
    if (now - lastSent.current < TYPING_THROTTLE) return
    lastSent.current = now
    mutation.mutate()
    // `mutation` is recreated each render; depending on it would rebuild this
    // callback on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nookId])
}

/**
 * Who is currently typing in this nook, excluding you.
 *
 * Local component state, deliberately not the Query cache: this is not server
 * state, nothing refetches it, and an indicator that outlived the typing would
 * be worse than none. Names expire on a timer.
 */
export function useTypingUsers(nookId: string, myId: string | undefined) {
  const [typists, setTypists] = useState<Record<string, { name: string; at: number }>>({})

  useEffect(() => {
    setTypists({})

    return subscribe(
      TypingStreamSubscription,
      { nookId },
      {
        onNext: ({ typingStream: event }) => {
          // Your own typing comes back to you over the same bus.
          if (event.user.id === myId) return
          setTypists((current) => ({
            ...current,
            [event.user.id]: { name: event.user.displayName, at: Date.now() },
          }))
        },
      },
    )
  }, [nookId, myId])

  // One timer for the whole set rather than one per person.
  useEffect(() => {
    if (Object.keys(typists).length === 0) return

    const timer = setInterval(() => {
      const cutoff = Date.now() - TYPING_TTL
      setTypists((current) => {
        const fresh = Object.fromEntries(
          Object.entries(current).filter(([, typist]) => typist.at > cutoff),
        )
        // Same object when nothing expired, so this does not re-render on tick.
        return Object.keys(fresh).length === Object.keys(current).length ? current : fresh
      })
    }, 1_000)

    return () => clearInterval(timer)
  }, [typists])

  return Object.values(typists).map((typist) => typist.name)
}

// ---------------------------------------------------------------------------
// Cache surgery. Pages are newest-first, so page 0 is where new messages land.
// ---------------------------------------------------------------------------

function editTimeline(
  client: QueryClient,
  nookId: string,
  change: (timeline: Timeline) => Timeline,
) {
  client.setQueryData<Timeline>(messageKeys.timeline(nookId), (current) =>
    current ? change(current) : current,
  )
}

function prependMessage(client: QueryClient, nookId: string, message: TimelineMessage) {
  editTimeline(client, nookId, (timeline) => {
    const known = timeline.pages.some((page) =>
      page.messages.messages.some((existing) => existing.id === message.id),
    )
    if (known) return timeline

    const [newest, ...rest] = timeline.pages
    if (!newest) return timeline

    return {
      ...timeline,
      pages: [
        {
          messages: {
            ...newest.messages,
            messages: [message, ...newest.messages.messages],
          },
        },
        ...rest,
      ],
    }
  })
}

/**
 * Change one message wherever it sits in the pages.
 *
 * Exported for `features/reactions`, which is the one other module allowed to
 * write into this cache — reactions are a field on a message, so they belong in
 * the message's own entry rather than in a store beside it.
 */
export function updateMessage(
  client: QueryClient,
  nookId: string,
  messageId: string,
  change: (message: TimelineMessage) => TimelineMessage,
) {
  editTimeline(client, nookId, (timeline) => ({
    ...timeline,
    pages: timeline.pages.map((page) => ({
      messages: {
        ...page.messages,
        messages: page.messages.messages.map((message) =>
          message.id === messageId ? change(message as TimelineMessage) : message,
        ),
      },
    })),
  }))
}

/**
 * Swap a pending message for the real one, from either direction.
 *
 * Called twice for every send — once by the socket echo and once by the
 * mutation's own response — and the three cases it has to be right about are:
 *
 * 1. **The pending row is here.** Replace it at its position in the page, so the
 *    message keeps its place in the order rather than being deleted and
 *    re-prepended. The React element is deliberately *not* preserved across this:
 *    see the key comment in `MessageTimeline`, where preserving it stranded every
 *    previous message at `opacity: 0`.
 * 2. **The real message is already here** and the pending row is gone, because
 *    the other caller got here first. Nothing to do.
 * 3. **Neither is here** — a send from another tab of ours, whose nonce means
 *    nothing in this cache. Insert it, exactly as an ordinary incoming message.
 *
 * Idempotent in all three, which is what lets both callers run unconditionally.
 */
function replaceMessage(
  client: QueryClient,
  nookId: string,
  messageId: string,
  replacement: TimelineMessage,
) {
  editTimeline(client, nookId, (timeline) => {
    const pending = timeline.pages.some((page) =>
      page.messages.messages.some((message) => message.id === messageId),
    )
    if (!pending) return timeline

    // The real message, with no `pending` or `failed` left on it.
    const settled: TimelineMessage = { ...replacement }

    return {
      ...timeline,
      pages: timeline.pages.map((page) => ({
        messages: {
          ...page.messages,
          messages: page.messages.messages.map((message) =>
            message.id === messageId ? settled : message,
          ),
        },
      })),
    }
  })

  // Case 3, and case 2's no-op: `prependMessage` already refuses an id it can
  // see, so this is only ever an insert for a message nothing here knew about.
  prependMessage(client, nookId, replacement)
}

function removeMessage(client: QueryClient, nookId: string, messageId: string) {
  editTimeline(client, nookId, (timeline) => ({
    ...timeline,
    pages: timeline.pages.map((page) => ({
      messages: {
        ...page.messages,
        messages: page.messages.messages.filter((message) => message.id !== messageId),
      },
    })),
  }))
}
